import { ApiError } from '../identity/types';
import {
  DEFAULT_ANNUAL_HOURS, MAX_ANNUAL_HOURS, SYNTHETIC_DATA_LABEL,
  type BenchmarkGeography, type BenchmarkPoint, type BenchmarkQuery, type BenchmarkResult, type BenchmarkSourceRegistryItem, type BenchmarkSourceResult,
  type PayBasis,
} from './compensationTypes';
import { convertAmount, roundMoney } from './payBand';
import { stateName } from './usStates';

/**
 * SYNTHETIC TEST DATA ONLY. Everything in this file is generated for the front-end mock gateway so the benchmark screens can be
 * exercised and screenshotted. There is not one real market wage here: every figure is a repeating-digit number (11.11, 22.22,
 * 44,444 ...) scaled by a small whole factor, and both sources are `kind: "Synthetic"` titled "EPN Synthetic Test Dataset (not market
 * data)". The UI shows a "SYNTHETIC TEST DATA" badge wherever these numbers appear. Real data enters only through the server's offline
 * import of a licensed dataset package, never through the browser.
 */

export const SYNTHETIC_TITLE = 'EPN Synthetic Test Dataset (not market data)';
const SYNTHETIC_ORG = 'EPN (synthetic test data generator)';
const SYNTHETIC_ATTRIBUTION = `${SYNTHETIC_DATA_LABEL}: generated by EPN for testing. These numbers are not market data.`;
const RETRIEVAL_DATE = '2026-01-15';
const LAST_UPDATED_UTC = '2026-01-15T09:00:00.000Z';

/** The mock's scenarios (sessionStorage `epn.mock.benchmarks`): `available` (default), `nodata` (no dataset imported), `none` (every role
 * behaves as having no SOC code), `forbidden` (403 and `NotPermitted`), `unavailable` (503 from the benchmark endpoints). */
export type BenchmarkScenario = 'available' | 'nodata' | 'none' | 'forbidden' | 'unavailable';
export const MOCK_BENCHMARKS_KEY = 'epn.mock.benchmarks';

export function parseScenario(value: string | null): BenchmarkScenario {
  return value === 'nodata' || value === 'none' || value === 'forbidden' || value === 'unavailable' ? value : 'available';
}

/** Whole-number scale per SOC code. Only these three occupations have synthetic rows; every other SOC code is "no data imported". */
const SCALE: Record<string, number> = { '43-3051': 1, '13-2011': 2, '11-3031': 3 };

export const hasSyntheticData = (socCode: string | null): boolean => socCode !== null && socCode in SCALE;

interface SyntheticSource {
  code: string;
  edition: string;
  basis: PayBasis;
  /** reference year -> geography key ("National" or "State:CA") -> percentile -> value at scale 1. */
  data: Record<number, Record<string, Record<number, number>>>;
}

const SOURCES: SyntheticSource[] = [
  {
    code: 'SYNTHETIC_A', edition: 'SYNTHETIC-A-2025', basis: 'Hourly',
    data: { 2025: { National: { 10: 11.11, 50: 22.22, 90: 33.33 }, 'State:CA': { 10: 12.12, 50: 24.24, 90: 36.36 } } },
  },
  {
    code: 'SYNTHETIC_B', edition: 'SYNTHETIC-B-2025', basis: 'Annual',
    data: {
      2025: { National: { 25: 44444, 50: 55555, 75: 66666 } },
      2024: { National: { 25: 33333, 50: 44444, 75: 55555 } },
    },
  },
];

const nth = (n: number): string => `${n}th`;

function geography(type: 'National' | 'State' | 'Metro', code: string | null): BenchmarkGeography {
  if (type === 'National') return { type, code: null, name: 'United States' };
  if (type === 'State') return { type, code, name: stateName(code) };
  return { type, code, name: `Metro area ${code ?? ''}`.trim() };
}

const problem = (status: number, detail: string, code: string | null = null) => new ApiError(status, detail, null, true, code);

/** What the server would answer for `GET /api/job-roles/{id}/compensation-benchmarks` under the `available`, `nodata` and `none` scenarios. */
export function lookupSyntheticBenchmarks(roleId: string, socCode: string | null, query: BenchmarkQuery, scenario: BenchmarkScenario): BenchmarkResult {
  const geographyType = query.geographyType ?? 'National';
  const basis = query.basis ?? 'Annual';
  const annualHours = query.annualHours ?? DEFAULT_ANNUAL_HOURS;
  if (!Number.isFinite(annualHours) || annualHours < 1 || annualHours > MAX_ANNUAL_HOURS) {
    throw problem(400, `The annual hours must be between 1 and ${MAX_ANNUAL_HOURS}.`, 'BENCHMARK_QUERY_INVALID');
  }
  if (geographyType === 'State' && !query.state) throw problem(400, 'Choose a state to look up state benchmarks.', 'BENCHMARK_QUERY_INVALID');
  if (geographyType === 'Metro' && !query.metroCode) throw problem(400, 'Enter a metro area code to look up metro benchmarks.', 'BENCHMARK_QUERY_INVALID');

  const request = {
    geographyType, state: query.state ?? null, metroCode: query.metroCode ?? null, referenceYear: query.referenceYear ?? null, basis, annualHours,
    annualHoursSource: query.annualHours === undefined ? 'default' : 'requested',
  };
  const empty = (status: 'NoSocCode' | 'NoData', message: string): BenchmarkResult => ({
    roleId, socCode, status, message, request, sources: [], excluded: [],
    suggestedRange: { status: 'Unavailable', reason: 'No aggregation methodology has been approved. Sources are shown separately.' },
    limitations: [], disclaimer: null,
  });

  if (!socCode || scenario === 'none') {
    return empty('NoSocCode', 'This role has no SOC code, so there is nothing to look up. Add a SOC code to the role to see market benchmarks.');
  }
  if (scenario === 'nodata' || !hasSyntheticData(socCode)) {
    return empty('NoData', 'No benchmark data has been imported for this occupation. Benchmarks stay unavailable until a licensed or approved dataset is imported.');
  }

  const scale = SCALE[socCode]!;
  const sources: BenchmarkSourceResult[] = [];
  const excluded: { sourceCode: string; reason: string }[] = [];

  for (const source of SOURCES) {
    const years = Object.keys(source.data).map(Number);
    const year = query.referenceYear ?? Math.max(...years);
    const byGeography = source.data[year];
    if (!byGeography) {
      excluded.push({ sourceCode: source.code, reason: `No data for reference year ${query.referenceYear}.` });
      continue;
    }

    // Geography fallback: Metro -> its State (when one was given) -> National, State -> National. Nothing is invented.
    let resolved: BenchmarkGeography = geography('National', null);
    let reason: string | null = null;
    const stateKey = query.state ? `State:${query.state}` : null;
    if (geographyType === 'Metro') {
      if (stateKey && byGeography[stateKey]) {
        resolved = geography('State', query.state ?? null);
        reason = 'No metropolitan data for this occupation in this source; showing the state benchmark.';
      } else {
        reason = 'No metropolitan or state data for this occupation in this source; showing the national benchmark.';
      }
    } else if (geographyType === 'State') {
      if (stateKey && byGeography[stateKey]) resolved = geography('State', query.state ?? null);
      else reason = 'No state data for this occupation in this source; showing the national benchmark.';
    }
    const key = resolved.type === 'State' ? `State:${resolved.code}` : 'National';
    const percentiles = byGeography[key]!;
    const reported = Object.keys(percentiles).map(Number).sort((a, b) => a - b);

    const labelled: { label: string; percentile: number }[] = [];
    const low = reported.find((p) => p === 10 || p === 25);
    const high = [...reported].reverse().find((p) => p === 75 || p === 90);
    if (low !== undefined) labelled.push({ label: 'Market Low', percentile: low });
    if (reported.includes(50)) labelled.push({ label: 'Market Median', percentile: 50 });
    if (high !== undefined) labelled.push({ label: 'Market High', percentile: high });

    const points: BenchmarkPoint[] = labelled.map(({ label, percentile }) => {
      const original = Math.round(percentiles[percentile]! * scale * 100) / 100;
      return { label, percentile, originalValue: original, value: roundMoney(convertAmount(original, source.basis, basis, annualHours)) };
    });

    sources.push({
      sourceCode: source.code, organization: SYNTHETIC_ORG, title: SYNTHETIC_TITLE, url: null, kind: 'Synthetic', licenseStatus: 'OpenLicense',
      attributionText: SYNTHETIC_ATTRIBUTION, datasetEdition: source.edition, referenceYear: year, retrievalDate: RETRIEVAL_DATE,
      lastUpdatedUtc: LAST_UPDATED_UTC, expirationDate: null, requestedGeography: geography(geographyType, geographyType === 'State' ? (query.state ?? null) : geographyType === 'Metro' ? (query.metroCode ?? null) : null),
      resolvedGeography: resolved, fallback: { applied: reason !== null, reason }, currency: 'USD', originalBasis: source.basis, convertedBasis: basis,
      points,
      percentileDefinition: `Market Low = ${nth(low ?? 0)} percentile, Market Median = 50th, Market High = ${nth(high ?? 0)}, as reported by the source.`,
      sampleSize: null, reliabilityNote: null, dataAvailability: labelled.length === 3 ? 'Full' : 'Partial',
    });
  }

  if (sources.length === 0) return { ...empty('NoData', 'No benchmark data matches the reference year you chose.'), excluded };

  return {
    roleId, socCode, status: 'Available', message: null,
    request: { ...request, referenceYear: query.referenceYear ?? Math.max(...sources.map((s) => s.referenceYear)) },
    sources, excluded,
    suggestedRange: { status: 'Unavailable', reason: 'No aggregation methodology has been approved. Sources are shown separately.' },
    limitations: [
      'Percentiles are survey estimates, not the lowest or highest pay that exists.',
      `${SYNTHETIC_DATA_LABEL}: these figures are generated by EPN for testing. They are not market data.`,
      'Hourly and annual values are converted only with the annual hours shown. No other adjustment is made.',
    ],
    disclaimer: 'Informational only. A benchmark is not a guaranteed salary, legal advice, or a wage-law determination, and it does not replace any applicable minimum wage.',
  };
}

const PLANNED: Pick<BenchmarkSourceRegistryItem, 'code' | 'organization' | 'title' | 'url' | 'kind'>[] = [
  { code: 'BLS_OEWS', organization: 'U.S. Bureau of Labor Statistics', title: 'Occupational Employment and Wage Statistics', url: 'https://www.bls.gov/oes/', kind: 'PublicGovernment' },
  { code: 'ONET', organization: 'O*NET Resource Center', title: 'Occupational information', url: 'https://www.onetcenter.org/', kind: 'PublicGovernment' },
  { code: 'CAREERONESTOP', organization: 'CareerOneStop', title: 'CareerOneStop', url: 'https://www.careeronestop.org/', kind: 'PublicGovernment' },
  { code: 'STATE_LMI', organization: 'State labor-market-information agencies', title: 'State labor-market information', url: null, kind: 'PublicGovernment' },
  { code: 'PRIVATE_LICENSED', organization: 'Licensed private study (placeholder)', title: 'Licensed private study', url: null, kind: 'Private' },
];

/** The registry as the server lists it: the planned sources, all disabled and pending review; in the `available` scenario the two synthetic sources are enabled. */
export function syntheticSourceRegistry(scenario: BenchmarkScenario): BenchmarkSourceRegistryItem[] {
  const planned: BenchmarkSourceRegistryItem[] = PLANNED.map((p) => ({
    ...p, licenseStatus: 'PendingReview', isEnabled: false, attributionText: null, usageNotes: 'Terms to be confirmed by legal before enabling.', latestBatch: null,
  }));
  if (scenario !== 'available') return planned;
  const synthetic: BenchmarkSourceRegistryItem[] = SOURCES.map((s) => ({
    code: s.code, organization: SYNTHETIC_ORG, title: SYNTHETIC_TITLE, url: null, kind: 'Synthetic', licenseStatus: 'OpenLicense', isEnabled: true,
    attributionText: SYNTHETIC_ATTRIBUTION, usageNotes: 'Synthetic test data for the mock gateway only. Never available in production.',
    latestBatch: {
      datasetEdition: s.edition, referenceYear: Math.max(...Object.keys(s.data).map(Number)), retrievalDate: RETRIEVAL_DATE,
      importedAtUtc: LAST_UPDATED_UTC, rowCount: 9, status: 'Imported',
    },
  }));
  return [...synthetic, ...planned];
}
